import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  TrendingUp, Eye, MousePointerClick, DollarSign, 
  CheckCircle, XCircle, Clock, BarChart3, Megaphone, ArrowRight, Settings 
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminMarketingPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending_approval');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/ads/admin/campaigns${filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`, {
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/ads/admin/campaigns/stats`, {
          credentials: 'include'
        })
      ]);

      if (campaignsRes.ok) {
        const campaignsData = await campaignsRes.json();
        setCampaigns(campaignsData.campaigns || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (campaignId) => {
    if (!confirm('Approve this campaign? It will start running immediately.')) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/campaigns/${campaignId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Campaign approved and now running');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve campaign');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedCampaign || !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/campaigns/${selectedCampaign.id}/reject?reason=${encodeURIComponent(rejectionReason)}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Campaign rejected and user refunded');
        setShowRejectModal(false);
        setRejectionReason('');
        setSelectedCampaign(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject');
      }
    } catch (error) {
      toast.error('Failed to reject campaign');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      running: 'bg-green-100 text-green-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
      paused: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Marketing & Advertising</h1>
          <p className="text-muted-foreground">Manage Matrise Ads paid campaigns & system promotions</p>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* System Promotions */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950 dark:to-blue-950 border-2 border-purple-200 dark:border-purple-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-lg">
                    <Megaphone className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">System Promotions</h3>
                    <p className="text-xs text-muted-foreground">Free campaigns</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate('/admin/marketing/promotions')} className="bg-purple-600 hover:bg-purple-700">
                  Manage
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Analytics */}
          <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 border-2 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Analytics</h3>
                    <p className="text-xs text-muted-foreground">Performance insights</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate('/admin/marketing/analytics')} className="bg-blue-600 hover:bg-blue-700">
                  View
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Settings */}
          <Card className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950 dark:to-amber-950 border-2 border-orange-200 dark:border-orange-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-100 dark:bg-orange-900 p-2 rounded-lg">
                    <Settings className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Settings</h3>
                    <p className="text-xs text-muted-foreground">Configure pricing</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate('/admin/marketing/settings')} className="bg-orange-600 hover:bg-orange-700">
                  Configure
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-yellow-600">{stats.pending_campaigns}</div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-green-600">{stats.running_campaigns}</div>
                <p className="text-sm text-muted-foreground">Running Campaigns</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-purple-600">
                  {stats.total_revenue_mtr.toLocaleString()} MTR
                </div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{stats.overall_ctr.toFixed(2)}%</div>
                <p className="text-sm text-muted-foreground">Overall CTR</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2 flex-wrap">
              {['pending_approval', 'running', 'all', 'expired', 'rejected'].map((status) => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                >
                  {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Campaigns List */}
        <div className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">Loading...</CardContent>
            </Card>
          ) : campaigns.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <p className="text-muted-foreground">No campaigns found</p>
              </CardContent>
            </Card>
          ) : (
            campaigns.map((campaign) => (
              <Card key={campaign.id}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Image */}
                    {campaign.image_url && (
                      <div className="w-full md:w-48 h-32 flex-shrink-0">
                        <img 
                          src={campaign.image_url} 
                          alt={campaign.headline}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                    )}

                    {/* Details */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold">{campaign.campaign_name}</h3>
                          <p className="text-sm text-muted-foreground">{campaign.business_name} · {campaign.user_email}</p>
                        </div>
                        <Badge className={getStatusColor(campaign.status)}>
                          {campaign.status.replace('_', ' ')}
                        </Badge>
                      </div>

                      <div className="mb-4">
                        <p className="font-semibold mb-1">{campaign.headline}</p>
                        <p className="text-sm text-muted-foreground mb-2">{campaign.description}</p>
                        <a 
                          href={campaign.destination_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {campaign.destination_link}
                        </a>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 text-sm">
                        <div>
                          <div className="text-xs text-muted-foreground">Placement</div>
                          <div className="font-semibold capitalize">{campaign.placement}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Duration</div>
                          <div className="font-semibold">{campaign.duration_days} days</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Spend</div>
                          <div className="font-semibold text-purple-600">
                            {campaign.total_cost.toLocaleString()} MTR
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Impressions</div>
                          <div className="font-semibold">{campaign.impressions || 0}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">CTR</div>
                          <div className="font-semibold">{campaign.ctr?.toFixed(2) || 0}%</div>
                        </div>
                      </div>

                      {/* Actions */}
                      {campaign.status === 'pending_approval' && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleApprove(campaign.id)}
                            disabled={processing}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setShowRejectModal(true);
                            }}
                            disabled={processing}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Reject Modal */}
        {showRejectModal && selectedCampaign && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-lg w-full">
              <CardHeader>
                <CardTitle>Reject Campaign</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">
                    <strong>{selectedCampaign.campaign_name}</strong>
                    <br />
                    User will be refunded {selectedCampaign.total_cost.toLocaleString()} MTR tokens
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Rejection Reason *</Label>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Explain why this campaign is being rejected..."
                    rows={4}
                    required
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectionReason('');
                      setSelectedCampaign(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleReject}
                    disabled={processing || !rejectionReason.trim()}
                  >
                    {processing ? 'Processing...' : 'Confirm Rejection'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
